import { useEffect, useState, type ComponentType } from "react"
import {
  PencilSquareIcon,
  CameraIcon,
  MapPinIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  DocumentCheckIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline"
import { Modal } from "../../components/modal/Modal"
import { UserProfile } from "./types"
import { uploadMyAvatarApi } from "../../features/profile/api/profile.api"

type Props = {
  profile: UserProfile
  onSave: (data: Partial<UserProfile>) => Promise<void> | void
  stats?: {
    experience: number
    education: number
    certificates: number
    portfolio: number
  }
}

export const ProfileSidebar = ({ profile, onSave, stats }: Props) => {
  const [openEdit, setOpenEdit] = useState(false)

  const [fullName, setFullName] = useState(profile.full_name)
  const [bio, setBio] = useState(profile.bio || "")
  const [city, setCity] = useState(profile.city || "")

  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState(
    withHttps(profile.avatar?.original_url) || ""
  )

  const [saving, setSaving] = useState(false)

  /* ── effects ── */

  useEffect(() => {
    setFullName(profile.full_name)
    setBio(profile.bio || "")
    setCity(profile.city || "")
  }, [profile])

  useEffect(() => {
    if (!avatarFile) return
    const objectUrl = URL.createObjectURL(avatarFile)
    setAvatarPreview(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [avatarFile])

  useEffect(() => {
    if (!avatarFile) {
      setAvatarPreview(withHttps(profile.avatar?.original_url) || "")
    }
  }, [profile.avatar?.original_url])

  /* ── helpers ── */

  const completion = calculateCompletion(profile, stats)
  const initials = profile.full_name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)

  const handleSave = async () => {
    if (!fullName.trim()) return
    try {
      setSaving(true)
      await onSave({ full_name: fullName, bio, city })
      setOpenEdit(false)
    } finally {
      setSaving(false)
    }
  }

  /* ── render ── */

  return (
    <div className="sticky top-6 space-y-4">

      {/* ── Main card ── */}
      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
        {/* Banner */}
        <div className="h-20 bg-gradient-to-r from-blue-500 to-violet-600" />

        {/* Avatar + name */}
        <div className="px-5 pb-5">
          {/* Avatar */}
          <label
            htmlFor="avatar-upload"
            className="relative w-20 h-20 -mt-10 block cursor-pointer group"
          >
            {avatarPreview ? (
              <img
                src={avatarPreview}
                className="w-20 h-20 rounded-2xl object-cover ring-4 ring-white shadow-sm"
              />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-400 to-violet-500 ring-4 ring-white shadow-sm flex items-center justify-center text-white text-xl font-bold">
                {initials}
              </div>
            )}
            <div className="absolute inset-0 rounded-2xl bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
              <CameraIcon className="w-5 h-5 text-white" />
            </div>
          </label>

          <input
            id="avatar-upload"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0]
              if (!file) return
              try {
                setSaving(true)
                setAvatarFile(file)
                await uploadMyAvatarApi(file)
              } finally {
                setSaving(false)
              }
            }}
          />

          <div className="mt-3">
            <h2 className="font-semibold text-gray-900 text-base">{profile.full_name}</h2>
            {(profile.industry || profile.experience_level) && (
              <p className="text-xs text-gray-500 mt-0.5 capitalize">
                {[profile.industry, profile.experience_level].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>

          {profile.bio && (
            <p className="text-sm text-gray-600 mt-2 leading-relaxed line-clamp-3">{profile.bio}</p>
          )}

          {profile.city && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-2 capitalize">
              <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
              {profile.city}
            </div>
          )}

          <button
            onClick={() => setOpenEdit(true)}
            className="mt-4 w-full flex justify-center items-center gap-2 rounded-lg border border-gray-200 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition"
          >
            <PencilSquareIcon className="w-4 h-4" />
            Edit profile
          </button>
        </div>
      </div>

      {/* ── Profile completion ── */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-700">Profile completion</span>
          <span className={`text-xs font-semibold ${completion >= 75 ? "text-emerald-600" : completion >= 50 ? "text-amber-600" : "text-blue-600"}`}>
            {completion}%
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${completion >= 75 ? "bg-emerald-500" : completion >= 50 ? "bg-amber-500" : "bg-blue-400"}`}
            style={{ width: `${completion}%` }}
          />
        </div>
        {completion < 100 && (
          <p className="text-xs text-gray-400 mt-2">
            {completion < 50 ? "Fill in more details to stand out" : "Almost there! Keep going"}
          </p>
        )}
      </div>

      {/* ── Stats ── */}
      {stats && (
        <div className="bg-white border border-gray-100 rounded-xl p-4 grid grid-cols-2 gap-3 shadow-sm hover:shadow-md transition-shadow duration-200">
          <StatItem icon={BriefcaseIcon} label="Experience" value={stats.experience} color="text-blue-500 bg-blue-50" />
          <StatItem icon={AcademicCapIcon} label="Education" value={stats.education} color="text-violet-500 bg-violet-50" />
          <StatItem icon={DocumentCheckIcon} label="Certificates" value={stats.certificates} color="text-emerald-500 bg-emerald-50" />
          <StatItem icon={PhotoIcon} label="Portfolio" value={stats.portfolio} color="text-amber-500 bg-amber-50" />
        </div>
      )}

      {/* ── Edit Modal ── */}
      <Modal open={openEdit} onClose={() => setOpenEdit(false)} title="Edit profile">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Full name</label>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Full name"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Bio</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="A short summary about yourself…"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={4}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">City</label>
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. Moscow"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={() => setOpenEdit(false)}
              className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !fullName.trim()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50 hover:bg-blue-700 transition"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

/* ── helpers ── */

const StatItem = ({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: ComponentType<{ className?: string }>
  label: string
  value: number
  color: string
}) => (
  <div className="flex items-center gap-2.5">
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon className="w-4 h-4" />
    </div>
    <div>
      <div className="text-sm font-semibold text-gray-900">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  </div>
)

const withHttps = (url?: string) => {
  if (!url) return undefined
  if (/^https?:\/\//i.test(url)) return url
  return `https://${url}`
}

const calculateCompletion = (profile: UserProfile, stats?: Props["stats"]) => {
  let total = 0
  let filled = 0

  const fields = [
    profile.full_name,
    profile.bio,
    profile.city,
    profile.avatar?.original_url,
  ]

  fields.forEach((f) => {
    total++
    if (f) filled++
  })

  if (stats) {
    total += 4
    if (stats.experience > 0) filled++
    if (stats.education > 0) filled++
    if (stats.certificates > 0) filled++
    if (stats.portfolio > 0) filled++
  }

  return Math.round((filled / total) * 100)
}
